import { useContext, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import type { ExternalNode } from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/external_node_pb";
import { OrgContext } from "contexts/OrgContext";
import { ExternalNodeService } from "@buf/brevdev_devplane.connectrpc_es/devplaneapi/v1/external_node_connect";
import { createConnectClient } from "server/connect";

const POLL_INTERVAL_MS = 10_000;
const externalNodeClient = createConnectClient(ExternalNodeService);

const brevConnectNodeQueryKey = (
  organizationId: string | undefined,
  registrationToken: string | null,
) => ["brev-connect-node", organizationId, registrationToken] as const;

interface UseBrevConnectNodePollingOptions {
  registrationToken: string | null;
  enabled: boolean;
  onNodeFound: (node: ExternalNode) => void;
}

export const useBrevConnectNodePolling = ({
  registrationToken,
  enabled,
  onNodeFound,
}: UseBrevConnectNodePollingOptions) => {
  const { activeOrgId } = useContext(OrgContext);
  const onNodeFoundRef = useRef(onNodeFound);
  const queryKey = brevConnectNodeQueryKey(activeOrgId, registrationToken);
  const pollingEnabled = Boolean(enabled && activeOrgId && registrationToken);
  const completedRegistrations = useRef(new Set<string>());
  const registrationQuery = useQuery({
    queryKey,
    queryFn: ({ signal }) =>
      externalNodeClient.getNodeRegistrationStatus(
        { registrationToken: registrationToken! },
        { signal },
      ),
    enabled: pollingEnabled,
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: (status) =>
      status?.registered ? false : POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });
  const externalNodeId = registrationQuery.data?.registered
    ? registrationQuery.data.externalNodeId
    : undefined;
  const nodeQuery = useQuery({
    queryKey: [...queryKey, "node", externalNodeId],
    queryFn: async ({ signal }) => {
      const response = await externalNodeClient.getNode(
        { externalNodeId: externalNodeId! },
        { signal },
      );
      if (!response.externalNode) {
        throw new Error("Registered compute was not returned");
      }
      return response.externalNode;
    },
    enabled: pollingEnabled && Boolean(externalNodeId),
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: (node) => (node ? false : POLL_INTERVAL_MS),
    refetchIntervalInBackground: false,
  });
  const registeredNode = nodeQuery.data;

  useEffect(() => {
    onNodeFoundRef.current = onNodeFound;
  }, [onNodeFound]);

  useEffect(() => {
    const completionKey = JSON.stringify([activeOrgId, registrationToken]);
    if (
      pollingEnabled &&
      registeredNode &&
      !completedRegistrations.current.has(completionKey)
    ) {
      completedRegistrations.current.add(completionKey);
      onNodeFoundRef.current(registeredNode);
    }
  }, [activeOrgId, registrationToken, pollingEnabled, registeredNode]);

  return {
    isError: registrationQuery.isError || nodeQuery.isError,
    refetch: () =>
      externalNodeId ? nodeQuery.refetch() : registrationQuery.refetch(),
  };
};
